import type { Ref } from "vue";
import type { ValidationArgs } from "@vuelidate/core";
import type { Form, InputData } from "@/composables/forms/formTypes";
import { ref, computed } from "vue";
import { omit } from "@/utils/utils";
import { v4 } from "uuid";
import useVuelidate from "@vuelidate/core";
import { mergeDiffs } from "@/utils/diff/mergeDiffs";

// These fields will be removed from the form data
// during the prepare() method.
const omittedFields = [
  "__typename",
  "winning_revision_id",
  "winning_revision_author_id",
  "parent_id",
  "journal_author_id",
  "journal_created_at",
  "journal_updated_at",
  "journal_deleted_at",
  "winning_revision_created_at",
  "winning_revision_updated_at",
  "winning_revision_deleted_at",
  "winning_revision_parent_id",
  "author_id" // For deviation survey only
];

// These fields will not
// show up in the form diff.
const keysToOmitFromDiff = [
  "parent_id",
  "cloned_from_journal_id",
  "journal_created_at",
  "journal_updated_at",
  "winning_revision_created_at",
  "winning_revision_updated_at",
  "winning_revision_parent_id",
  (key: string) => key.startsWith("r_")
];

// These fields will not show up in the form diff
// if they are internally null, defaulted to 0, and manually changed to a 0-equivalent value.
// Examples: null -> 0, null -> 00, null -> 0.0, null -> 1 -> 0
// Dev note: assume these will be null on a new Sizing/Scenario creation.
const initiallyNullFields = new Set([
  "top_of_perforations_measured_depth_feet",
  "top_of_perforations_true_vertical_depth_feet",
  "well_test_datum_point_measured_depth_feet",
  "well_test_oil_rate_stb_day",
  "well_test_water_rate_stb_day",
  "well_test_gas_rate_mscf_day",
  "well_test_static_bottom_hole_pressure_psia",
  "well_test_producing_bottom_hole_pressure_psia",
  "bubble_point_pressure_psia",
  "fluid_viscosity_override_cp"
]);

/**
 * "Normalize" data temporarily by setting initially null fields to 0
 * such that the 'Show Diff' feature accurately compares against 0 equivalent values.
 * @param data The UI form and/or hasura DB data to normalize.
 * @returns a temporary '0' in place of null values.
 */
const normalizeForDiff = (
  data: Record<string, unknown> | null | undefined
): Record<string, unknown> => {
  const normalized = { ...(data ?? {}) };
  initiallyNullFields.forEach(field => {
    if (normalized[field] == null) {
      normalized[field] = 0;
    }
  });

  return normalized;
};

function useForm<T extends InputData>(
  initialData: Ref<T | undefined | null>,
  rules?: ValidationArgs<Partial<T>>
): Form<T> {
  const data = ref({
    ...initialData.value
  }) as Ref<T>;
  // Derived defaults can participate in calculations without becoming edits
  // or being persisted until a normal update promotes them.
  const baselineOverrides = ref({}) as Ref<Partial<T>>;

  const V =
    rules && data.value
      ? useVuelidate(rules, data, {
          $scope: false
        })
      : null; // No rules provided to useForm, Vuelidate is set to null

  const diff = computed(() => {
    const lhs = normalizeForDiff({
      ...initialData.value,
      ...baselineOverrides.value
    });
    const rhs = normalizeForDiff(data.value);

    return mergeDiffs(lhs, rhs, keysToOmitFromDiff);
  });

  const reset = (): void => {
    baselineOverrides.value = {};
    data.value = {
      ...initialData.value
    } as T;
  };

  const transformData = (data: T): Record<string, unknown> => {
    // Narrow for data that uses revision structure
    if ("winning_revision_id" in data) {
      const flatData = omit(
        data,
        /* @ts-ignore */ /* eslint-disable-line @typescript-eslint/ban-ts-comment */
        (value, key) =>
          // Omit table relationships and nested data
          (key as string).startsWith("r_") ||
          // Omits all fields specified in omittedFields variable
          omittedFields.includes(key as string)
      );
      return {
        ...flatData,
        id: v4(),
        journal_id: data.id || v4(),
        parent_revision_id: data.winning_revision_id,
        deleted_at: data.journal_deleted_at
      };
    } else {
      return omit(
        data,
        (v, k) =>
          // Omit table relationships and nested data
          (k as string).startsWith("r_") ||
          // Omits all fields specified in omittedFields variable
          omittedFields.includes(k as string)
      ) as Record<string, unknown>;
    }
  };

  const getPersistentData = (): T => {
    const persistentData = {
      ...data.value
    } as T;
    for (const key of Object.keys(baselineOverrides.value) as (keyof T)[]) {
      if (
        initialData.value &&
        Object.prototype.hasOwnProperty.call(initialData.value, key)
      ) {
        persistentData[key] = initialData.value[key];
      } else {
        delete persistentData[key];
      }
    }
    return persistentData;
  };

  const prepare = () => {
    return {
      ...transformData(getPersistentData())
    };
  };

  const prepareDiffsAsArray = () => {
    return Object.keys(diff.value).map(key =>
      transformData(data.value[key as keyof InputData])
    );
  };

  const prepareAllAsArray = () => {
    const persistentData = getPersistentData();
    return Object.keys(persistentData).map(key =>
      transformData(persistentData[key as keyof InputData])
    );
  };
  const update = (updatedData: Partial<T>) => {
    for (const key of Object.keys(updatedData) as (keyof T)[]) {
      delete baselineOverrides.value[key];
    }
    Object.assign(data.value, updatedData);
  };
  const updateWithoutDiff = (updatedData: Partial<T>) => {
    Object.assign(baselineOverrides.value, updatedData);
    Object.assign(data.value, updatedData);
  };

  return {
    initialData,
    data,
    V,
    diff,
    reset,
    update,
    updateWithoutDiff,
    prepare,
    prepareAllAsArray,
    prepareDiffsAsArray
  };
}

export default useForm;
